import React, { useState, useEffect } from 'react';
import { FiDollarSign, FiFilter, FiCalendar,
         FiAlertCircle, FiFileText, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { api } from '@crm/shared';
import { C, fmtAmt } from '../shared/tokens';
import { Sh, Kpi, Empty } from '../shared/ui';
import { exportCsv, exportExcel, ExportButton } from '../shared/ExportButton';

const PAGE = 20;

const STAGE_MAP = {
  FOLLOW_UP:'Follow-up', LOGIN:'Login', SANCTION:'Sanction',
  DISBURSEMENT:'Disbursement', PDD:'PDD', REVENUE:'Revenue', COMPLETE:'Completed', CLOSED:'Closed',
};

const fmtD = iso => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
  } catch { return iso; }
};

export default function DisbursementReport({ filters = {}, loadTrigger = 0, setLoading, searched }) {
  const { year, month, selUser, dateFrom, dateTo, location, users = [] } = filters;

  const [rows,      setRows]      = useState([]);
  const [localLoad, setLocalLoad] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error,     setError]     = useState(null);
  const [page,      setPage]      = useState(1);
  const hasDateRange = Boolean(dateFrom && dateTo);

  const load = async () => {
    if (!year || !month) return;
    setLocalLoad(true); setLoading?.(true); setError(null); setPage(1);
    try {
      const yNum = parseInt(year);
      const mNum = parseInt(month);
      const rangeStart = hasDateRange ? new Date(dateFrom + 'T00:00:00') : new Date(yNum, mNum - 1, 1);
      const rangeEnd   = hasDateRange ? new Date(dateTo + 'T23:59:59') : new Date(yNum, mNum, 0, 23, 59, 59);

      // Fetch all disbursements for this month - API now returns embedded lead data
      const allDisb = [];
      let pg = 1;
      while (pg <= 100) {
        const res = await api.getLeadDisbursements({ 
          page: pg, 
          page_size: 100,
          size: 100,
          disbursement_date_after: rangeStart.toISOString().split('T')[0],
          disbursement_date_before: rangeEnd.toISOString().split('T')[0],
        });
        const d = res?.data;
        const results = Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
        allDisb.push(...results);
        if (!d?.next || results.length === 0) break;
        pg++;
      }

      // Lead data is already embedded in API response - use directly
      let merged = allDisb;

      // User filter - check lead_created_by and lead_internal_staff
      if (selUser) {
        merged = merged.filter(r => {
          const cbId = r.lead_created_by?.id;
          const csId = r.lead_internal_staff?.id;
          return String(cbId) === String(selUser) || String(csId) === String(selUser);
        });
      }

      // Location filter
      if (location?.trim()) {
        const q = location.trim().toLowerCase();
        merged = merged.filter(r => (r.lead_detail?.location || r.lead_detail?.city || '').toLowerCase().includes(q));
      }

      setRows(merged);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load disbursement data');
    } finally { setLocalLoad(false); setLoading?.(false); }
  };

  useEffect(() => { if (loadTrigger > 0) load(); }, [loadTrigger]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [loadTrigger]);

  const handleExport = fmt => {
    if (!rows.length) return;
    setExporting(true);
    try {
      const getUser = r => {
        const u = r.lead_created_by || {};
        return [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
      };
      const COLS = ['#','Lead No','Customer','Mobile','Product','Institution','Channel','Disb Date','Disb Type','Disb Amount','Income (Auto)','Insurance Amt','Stage','User','Location','Lead Source'];
      const data = rows.map((r, i) => {
        const lead = r.lead_detail || {};
        return [
          i+1,
          lead.lead_no || `#${r.lead_detail?.id || r.lead}`,
          lead.customer_name || '',
          lead.mobile_number || '',
          lead.product || '',
          lead.institution || '',
          lead.channel || '',
          r.disbursement_date || '',
          r.disbursement_type || '',
          r.amount || '',
          r.auto_income ?? '',
          r.insurance_amount || '',
          lead.current_stage || '',
          getUser(r),
          lead.location || '',
          lead.lead_source || ''
        ];
      });
      
      // Build title with user filter info
      const periodTitle = hasDateRange ? `${fmtD(dateFrom)} - ${fmtD(dateTo)}` : monthLabel;
      let title = `Disbursement Report - ${periodTitle}`;
      if (selUser && users.length > 0) {
        const selUserName = users.find(u => String(u.id) === String(selUser));
        if (selUserName) {
          const name = [selUserName.first_name, selUserName.last_name].filter(Boolean).join(' ') || selUserName.email;
          title = `Disbursement Report - ${name} - ${periodTitle}`;
        }
      }
      
      const fname = `disbursement_report_${year}_${month}`;
      if (fmt === 'excel') exportExcel(COLS, data, fname, title);
      else exportCsv(COLS, data, fname, title);
    } finally { setExporting(false); }
  };

  const totalAmt    = rows.reduce((s, r) => s + parseFloat(r.amount || 0), 0);
  const totalIncome = rows.reduce((s, r) => s + parseFloat(r.auto_income || 0), 0);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE));
  const visible    = rows.slice((page - 1) * PAGE, page * PAGE);

  const monthLabel = (() => {
    try { return new Date(`${year}-${String(month).padStart(2,'0')}-01`).toLocaleString('en-IN', { month:'long', year:'numeric' }); }
    catch { return `${month}/${year}`; }
  })();

  if (!searched) return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'56px 24px', textAlign:'center', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
      <div style={{ width:56, height:56, borderRadius:'50%', background:C.greenL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
        <FiDollarSign size={24} style={{ color:C.greenB, opacity:0.7 }}/>
      </div>
      <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select a month and click "Load Report"</div>
      <div style={{ fontSize:12.5, color:C.t3 }}>Shows all Disbursement cases for the selected period</div>
    </div>
  );

  if (localLoad) return (
    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {[0,1,2,3,4].map(i => <Sh key={i} h={48} r={8}/>)}
    </div>
  );

  if (error) return (
    <div style={{ background:C.redL, border:`1px solid ${C.redBord}`, borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:8 }}>
      <FiAlertCircle size={14} style={{ color:C.redB, flexShrink:0 }}/>
      <span style={{ fontSize:13, color:C.redB }}>{error}</span>
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
        <Kpi label="Total Cases"     value={rows.length}       icon={<FiFileText size={15}/>}   iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={false} sub={hasDateRange ? `${fmtD(dateFrom)} - ${fmtD(dateTo)}` : monthLabel}/>
        <Kpi label="Total Disbursed" value={fmtAmt(totalAmt)}  icon={<FiDollarSign size={15}/>} iconBg={C.navyL}  iconColor={C.navy}   accent={C.navy}   loading={false}/>
        <Kpi label="Auto Income"     value={fmtAmt(totalIncome)} icon={<FiDollarSign size={15}/>} iconBg='#EFF6FF' iconColor={C.navy}  accent={C.navy}   loading={false} sub="Disb × Inst% × Ch%"/>
        <Kpi label="Active Filters"  value={[selUser, hasDateRange, location].filter(Boolean).length || 'None'} icon={<FiFilter size={15}/>} iconBg={C.goldL} iconColor={C.gold} accent={C.goldB} loading={false} sub="User / Date / Location"/>
      </div>

      {rows.length === 0 ? (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'40px 24px', textAlign:'center' }}>
          <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>No disbursement cases found</div>
          <div style={{ fontSize:12.5, color:C.t3 }}>for {hasDateRange ? `${fmtD(dateFrom)} - ${fmtD(dateTo)}` : monthLabel}</div>
        </div>
      ) : (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ padding:'11px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FiDollarSign size={14} style={{ color:C.greenB }}/>
              <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Disbursement Cases — {hasDateRange ? `${fmtD(dateFrom)} - ${fmtD(dateTo)}` : monthLabel}</span>
              <span style={{ background:C.greenL, color:C.greenB, padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:700, border:`1px solid ${C.greenB}30` }}>{rows.length}</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {totalPages > 1 && <span style={{ fontSize:12, color:C.t4 }}>Page {page} / {totalPages}</span>}
              <ExportButton onExport={handleExport} loading={exporting} size="sm"/>
            </div>
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:900 }}>
              <thead>
                <tr style={{ background:'#F7F9FB' }}>
                  {['#','Lead No','Customer','Mobile','Product','Institution','Disb Date','Disb Type','Disb Amount','Income','Ins. Amount','Stage','User','Location'].map((h, i) => (
                    <th key={h} style={{ padding:'9px 13px', fontSize:10.5, fontWeight:800, color:C.t2, textTransform:'uppercase', letterSpacing:'0.07em', textAlign:i===8||i===9||i===10?'right':'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map((r, idx) => {
                  const lead   = r.lead_detail || {};
                  const isLast = idx === visible.length - 1;
                  const uDet   = r.lead_created_by || {};
                  const uName  = [uDet.first_name, uDet.last_name].filter(Boolean).join(' ') || '—';
                  const stgBg  = { DISBURSEMENT:C.greenL, PDD:C.navyL, REVENUE:C.tealL, CLOSED:C.borderL, PDD_PENDING:C.purpleL };
                  const stgFg  = { DISBURSEMENT:C.greenB, PDD:C.navy,  REVENUE:C.tealB, CLOSED:C.t3, PDD_PENDING:C.purpleB };
                  const dtc    = r.disbursement_type === 'FULL' ? { bg:C.greenL, color:C.greenB } : r.disbursement_type === 'PARTIAL' ? { bg:C.goldL, color:'#92400E' } : { bg:C.borderL, color:C.t3 };
                  return (
                    <tr key={r.id} style={{ background:idx%2===0?C.white:'#FAFBFC' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F0F9F4'}
                      onMouseLeave={e => e.currentTarget.style.background = idx%2===0?C.white:'#FAFBFC'}>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4 }}>{(page-1)*PAGE+idx+1}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><span style={{ fontSize:12, fontWeight:700, color:C.navy, fontFamily:'monospace' }}>{lead.lead_no||`#${r.lead}`}</span></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:600, color:C.t1 }}>{lead.customer_name||'—'}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>{lead.mobile_number||'—'}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{lead.product || '—'}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}>{lead.institution || '—'}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><div style={{ display:'flex', alignItems:'center', gap:5, fontSize:12.5, fontWeight:600, color:C.t1 }}><FiCalendar size={11} style={{ color:C.t4 }}/>{fmtD(r.disbursement_date)}</div></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><span style={{ padding:'2px 9px', borderRadius:20, fontSize:11, fontWeight:700, background:dtc.bg, color:dtc.color }}>{r.disbursement_type||'—'}</span></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}><span style={{ fontSize:13, fontWeight:800, color:C.greenB }}>{r.amount ? fmtAmt(r.amount) : '—'}</span></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right', background:'#EFF6FF' }}><span style={{ fontSize:12.5, fontWeight:700, color:C.navy }}>{r.auto_income != null && r.auto_income > 0 ? fmtAmt(r.auto_income) : '—'}</span></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right', fontSize:12, color:C.t2 }}>{r.insurance_amount && Number(r.insurance_amount) > 0 ? fmtAmt(r.insurance_amount) : '—'}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700, background:stgBg[lead.current_stage]||C.goldL, color:stgFg[lead.current_stage]||C.gold }}>{STAGE_MAP[lead.current_stage]||lead.current_stage||'—'}</span></td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12.5, fontWeight:600, color:C.t1 }}>{uName}</td>
                      <td style={{ padding:'9px 13px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}>{lead.location||lead.city||'—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ padding:'10px 18px', borderTop:`1px solid ${C.borderL}`, display:'flex', justifyContent:'space-between', alignItems:'center', background:'#FAFBFC' }}>
              <span style={{ fontSize:12.5, color:C.t4 }}>{(page-1)*PAGE+1}–{Math.min(page*PAGE, rows.length)} of {rows.length}</span>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`, background:page<=1?C.borderL:C.white, color:page<=1?C.t4:C.t2, cursor:page<=1?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><FiChevronLeft size={13}/></button>
                {Array.from({length:totalPages},(_,i)=>i+1).filter(p=>p===1||p===totalPages||Math.abs(p-page)<=1).reduce((acc,p,i,arr)=>{ if(i>0&&p-arr[i-1]>1)acc.push('…'); acc.push(p); return acc; },[]).map((p,i)=>p==='…'?<span key={`e${i}`} style={{padding:'0 4px',color:C.t4,lineHeight:'32px'}}>…</span>:<button key={p} onClick={()=>setPage(p)} style={{ width:32,height:32,borderRadius:7,border:`1px solid ${p===page?C.navy:C.border}`,background:p===page?C.navy:C.white,color:p===page?'#fff':C.t2,cursor:'pointer',fontSize:12,fontWeight:p===page?700:400 }}>{p}</button>)}
                <button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} style={{ width:32, height:32, borderRadius:7, border:`1px solid ${C.border}`, background:page>=totalPages?C.borderL:C.white, color:page>=totalPages?C.t4:C.t2, cursor:page>=totalPages?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}><FiChevronRight size={13}/></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
